import React from "react";
import { Download, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useInstallPrompt, useIsPWAInstalled, detectPlatform } from "../lib/pwa";
import { shareOrigin } from "../lib/api";

/**
 * One-tap install call-to-action.
 *
 * When tapped, the button decides the fastest possible next step per
 * platform. There are no step-by-step instructions on-screen — the
 * button IS the flow.
 *
 *   • Already installed → open the app directly (/dashboard). Runs in
 *     the standalone PWA when the user hits it from the home-screen
 *     icon; falls back to the browser tab otherwise (still opens the
 *     dashboard).
 *   • Chrome/Edge/Android with a captured beforeinstallprompt →
 *     `event.prompt()` — the browser's native OS-level install dialog.
 *   • iOS Safari (no programmatic install exists per Apple) →
 *     `navigator.share({ url })` — surfaces the native iOS Share Sheet
 *     where "Add to Home Screen" is a first-class option. This is the
 *     closest thing to a one-tap install that iOS Safari allows.
 *   • Desktop / other browsers without prompt → a single toast pointing
 *     at the address-bar install icon.
 *
 * The slogan renders above the button. No text renders below.
 */
export default function InstallCTA({
  slogan = "If they're Important, Keep them in Proximity",
}) {
  const navigate = useNavigate();
  const { available, prompt } = useInstallPrompt();
  const installed = useIsPWAInstalled();

  const onClick = async () => {
    // 1) Installed → open app immediately.
    if (installed) { navigate("/dashboard"); return; }

    // 2) Optimistic detection: another Chrome path may already list us
    //    as a related installed app even if display-mode reports browser.
    try {
      const rel = navigator.getInstalledRelatedApps
        ? await navigator.getInstalledRelatedApps()
        : [];
      if (rel && rel.length) { navigate("/dashboard"); return; }
    } catch { /* not supported — continue */ }

    // 3) Native install prompt (Chrome/Edge/Android).
    if (available) {
      const outcome = await prompt();
      if (outcome === "accepted") navigate("/dashboard");
      return;
    }

    // 4) iOS Safari: fire the OS Share Sheet directly.
    const platform = detectPlatform();
    if (platform === "ios" && typeof navigator.share === "function") {
      try {
        await navigator.share({
          title: "Proximity",
          text: "Install Proximity",
          url: shareOrigin(),
        });
      } catch {
        // User cancelled the share sheet — silent.
      }
      return;
    }

    // 5) Desktop / other browsers → a single, unobtrusive toast (no wall of text).
    toast.message("Look for the install icon in your browser's address bar.");
  };

  return (
    <div data-testid="install-cta" className="text-center">
      <p
        data-testid="install-slogan"
        className="font-display font-black text-2xl sm:text-3xl leading-tight tracking-tighter"
      >
        {slogan}
      </p>
      <button
        data-testid="install-cta-btn"
        onClick={onClick}
        className="mt-6 w-full sm:w-auto sm:min-w-[280px] inline-flex items-center justify-center gap-2 bg-[#007AFF] hover:bg-[#005ec4] text-white font-bold px-8 py-4 rounded-full text-base shadow-lg shadow-[#007AFF]/25 hover:shadow-[#007AFF]/45 transition-all"
      >
        {installed ? (<><ShieldCheck size={18}/> Open Proximity</>) : (<><Download size={18}/> Click here to install</>)}
      </button>
    </div>
  );
}
